import React, { useEffect, useState } from 'react';
import Yup from '~yup';
import { useFormik } from 'formik';

import Text from '~components/atoms/Text';
import Checkbox from '~components/atoms/Checkbox';
import ActionButton from '~components/atoms/ActionButton';
import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';

import { TDropdownOption } from '~types/components/labelledDropdown';
import { BranchStatusEnum, GoldTypeEnum, MetalTypeEnum, SilverTypeEnum } from '~utils/enums';

import * as FormStyles from './_createStockOutForm.module.scss';
import ConfirmStockoutTransactionsModal from '../ConfirmStockoutTransactionModal';

const CreateStockOutForm = () => {
    const dispatch = useAppDispatch();

    const productList = useAppSelector((state) => state.gold.goldInventory.inventoryListing.data);
    const serialNumberList = useAppSelector((state) => state.gold.allSerialNumbers);
    const serialNumberFilterParams = useAppSelector((state) => state.gold.params.serialNumberFilterParams);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);
    const filters = useAppSelector((state) => state.gold.params.inventoryFilterParams);
    const isLoading = useAppSelector((state) => state.gold.loadings.createStockOut);

    const isOpen = useAppSelector(state => state.gold.modals.isStockOutTransactionsModalOpen);
    const isCheckTransactions = useAppSelector((state) => state.gold.loadings.getStockOutTransactionsListing);

    const handleOpenModal = () => dispatch(combinedActions.goldSetIsStockOutTransactionsModalOpen(true));
    const handleCloseModal = () => dispatch(combinedActions.goldSetIsStockOutTransactionsModalOpen(false));

    const [productName, setProductName] = useState('');

    const [selectedProduct, setSelectedProduct] = useState<typeof productList[number] | null>(null);
    const [selectedSerialNumber, setSelectedSerialNumber] = useState<number[]>([]);
    const currentSerialNumberList =
        serialNumberList.find(
            (serialNumbers) => serialNumbers[0]?.productId === selectedProduct?.productId
        ) ?? [];

    const CreateStockOutFormValidation = Yup.object().shape({
        productName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Product Name is required'),
        amount: Yup.number().min(1, 'Quantity must be more than 0').typeError('Quantity must be a number').required('Amount is required'),
        serialNo: Yup.array().of(Yup.string()),
        branchName: Yup.string().
            matchNoLeadingAndTrailingWhiteSpaces()
            .matchMaxOneWhiteSpace()
            .test('check-branch-name',
                'Selected branch does not exist',
                function (value) {
                    if (!value) return false;
                    return branchOptions.some((branch) => branch.label === value);
                }
            )
            .required('Branch name is required'),
        branchCode: Yup.string().required('Branch code is required'),
        metalType: Yup.number()
            .required('Metal Type is required')
            .min(1, 'Metal Type is required'),
    });

    const CreateStockOutFormInitialValues = {
        productName: '',
        amount: '',
        branchName: '',
        branchCode: '',
        metalType: '',
    };

    const isPhysicalInventoryItem = (product?: typeof productList[number] | null) => {
        if (!product) return false;

        if (product.goldType !== undefined && product.goldType !== null) {
            return product.goldType === GoldTypeEnum.PhysicalGold;
        }

        if (product.silverType !== undefined && product.silverType !== null) {
            return product.silverType === SilverTypeEnum.PhysicalSilver;
        }

        return false;
    };

    const formik = useFormik({
        initialValues: CreateStockOutFormInitialValues,
        validationSchema: CreateStockOutFormValidation,
        onSubmit: (values) => {
            if (!values.branchCode || !selectedProduct) return;

            const isPhysical = isPhysicalInventoryItem(selectedProduct);

            const branch = branchesList.find(
                (b) => b.branchCode === values.branchCode
            );

            if (!branch) return;

            const payload = {
                branchId: branch.branchId,
                productId: selectedProduct.productId,
                amount: Number(values.amount),
                ...(isPhysical ? { serialNoId: selectedSerialNumber } : {}),
            };

            dispatch(combinedActions.goldSetCreateStockOutParams(payload));

            dispatch(
                combinedActions.goldSetStockOutsTransactionsFilters({
                    productId: payload.productId,
                    amount: payload.amount,
                    ...(isPhysical ? { serialNoId: selectedSerialNumber } : {}),
                })
            );

            dispatch(combinedActions.goldGetStockOutsTransactionsAttempt());

            // handleOpenModal();
        },
    });

    const productOptions: TDropdownOption[] = productList
        .map((product) => ({
            label: product.productName,
            value: String(product.productId),
        }))
        .sort((a, b) => Number(a.value) - Number(b.value));

    const handleOnSearchProduct = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const value = event.target.value;
        setProductName(value);
        formik.setFieldValue('productName', value);

        const product = productList.find(
            (p) => p.productName === value,
        );

        setSelectedProduct(product ?? null);

        formik.setFieldValue(
            'productId',
            product ? String(product.productId) : '',
        );
    };

    const handleOnSelectProduct = (option: TDropdownOption) => {
        const product = productList.find(
            (p) => String(p.productId) === String(option.value),
        );

        if (!product) return;

        setSelectedProduct(product);
        setProductName(product.productName);
        formik.setFieldValue('productName', product.productName);
        formik.setFieldValue('productId', String(product.productId));
    };

    useEffect(() => {
        if (branchesList.length === 0) return;
        if (formik.values.branchName) return;
        if (allBranchAccess) return;

        const defaultBranch = branchesList.find(
            b => b.branchCode === branchCode
        );

        if (!defaultBranch) return;

        formik.setFieldValue('branchName', defaultBranch.branchName);
        formik.setFieldValue('branchCode', defaultBranch.branchCode);

        dispatch(combinedActions.goldSetInventoryFilterParams({
            ...filters,
            branchCode: defaultBranch.branchCode,
            metalType: MetalTypeEnum.Gold,
        }));

        dispatch(combinedActions.goldGetInventoryStockListingAttempt());

    }, [branchesList]);

    useEffect(() => {
        if (selectedProduct) {
            setSelectedSerialNumber([]);
            const updatedFilterParams = {
                ...serialNumberFilterParams,
                productId: selectedProduct.productId,
                branchCode: formik.values.branchCode,
            };
            dispatch(combinedActions.goldSetSerialNumberFilterParams(updatedFilterParams));
            dispatch(combinedActions.goldGetAllSerialNumbersAttempt());
            formik.setFieldValue('amount', '');
            formik.setFieldValue('serialNo', []);
        }
    }, [selectedProduct]);

    useEffect(() => {
        if (selectedSerialNumber.length > 0) {
            formik.setFieldValue('amount', selectedSerialNumber.length.toString());
            formik.setFieldValue('serialNo', selectedSerialNumber);
        } else {
            formik.setFieldValue('amount', '');
            formik.setFieldValue('serialNo', []);
        }
    }, [selectedSerialNumber]);

    const handleOnSelectSerialNumber = (id: number) => {
        if (selectedSerialNumber.includes(id)) {
            setSelectedSerialNumber(
                selectedSerialNumber.filter(item => item !== id)
            );
        } else {
            setSelectedSerialNumber([...selectedSerialNumber, id]);
        }
    };

    const handleOnSelectAllSerialNumber = () => {
        if (selectedSerialNumber.length === currentSerialNumberList.length) {
            setSelectedSerialNumber([]);
        } else {
            setSelectedSerialNumber(
                currentSerialNumberList.map((serialNumber) => serialNumber.id)
            );
        }
    };

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active).map(branch => {
        return {
            label: branch.branchName,
            value: branch.branchCode,
        };
    });

    useEffect(() => {
        // Note: Not batching this setFieldValue with branchName's onChange setFieldValue because of a race condition
        // the branchName validation will fire first before the update is done, rendering an error for the branchName field
        const newBranch = branchesList.find(branch => branch.branchName === formik.values.branchName);

        if (newBranch) {
            formik.setFieldValue('branchCode', newBranch?.branchCode);
        } else {
            formik.setFieldValue('branchCode', '');
        }
    }, [formik.values.branchName]);

    const handleOnSearchBranch = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('branchName', event.target.value);
    };

    const handleOnSelectBranch = (option: TDropdownOption) => {
        const branchCodeVal = option.value ? String(option.value) : undefined;
        const branchNameVal = option.label;

        formik.setFieldValue('branchCode', branchCodeVal);
        formik.setFieldValue('branchName', branchNameVal);

        const selectedMetalType = formik.values.metalType
            ? Number(formik.values.metalType)
            : MetalTypeEnum.Gold;

        dispatch(combinedActions.goldSetInventoryFilterParams({
            ...filters,
            branchCode: branchCodeVal,
            metalType: selectedMetalType,
        }));

        dispatch(combinedActions.goldGetInventoryStockListingAttempt());
    };

    const renderSerialNumbers = () => {
        if (serialNumberList && serialNumberList.length > 0) {
            return currentSerialNumberList.map((serialNumber) => (
                <div
                    className={FormStyles['serial-number']}
                    key={serialNumber.itemCode ?? serialNumber.serialNo}
                >
                    <Checkbox
                        isReversed
                        label={serialNumber.serialNo}
                        isChecked={selectedSerialNumber.includes(serialNumber.id)}
                        onChange={() => handleOnSelectSerialNumber(serialNumber.id)}
                    />
                </div>
            ));
        }
    };

    const renderSerialNumberList = () => {
        if (isPhysicalInventoryItem(selectedProduct)) {
            return (
                <div className={FormStyles['serial-container']}>
                    <Text
                        isBold
                        label={'Serial Numbers* :'}
                        className={FormStyles['title']}
                    />

                    <div className={FormStyles['serial-list']}>
                        <Checkbox
                            isReversed
                            label='Select All'
                            isChecked={
                                selectedSerialNumber.length === currentSerialNumberList.length &&
                                currentSerialNumberList.length > 0
                            }
                            onChange={handleOnSelectAllSerialNumber}
                        />

                        {renderSerialNumbers()}
                    </div>
                </div>
            );
        }
        return null;
    };

    const metalTypeOptions = [
        { label: 'Gold', value: String(MetalTypeEnum.Gold) },
        { label: 'Silver', value: String(MetalTypeEnum.Silver) },
    ];

    return (
        <form onSubmit={formik.handleSubmit} className={FormStyles.container}>
            <div className={FormStyles['form-container']}>
                <LabelledDropdown
                    id='branchName'
                    name='branchName'
                    label='Branch*: '
                    options={branchOptions}
                    value={formik.values.branchName}
                    error={formik.touched.branchName ? formik.errors.branchName : undefined}
                    onChange={handleOnSearchBranch}
                    onSelectOption={handleOnSelectBranch}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No branches found'
                    autoComplete='off'
                    optionSelected={!!branchOptions.find((branch) => branch.label === formik.values.branchName)}
                    disabled={!allBranchAccess}
                />

                <LabelledInput
                    id='branchCode'
                    name='branchCode'
                    label='Branch Code* :'
                    value={formik.values.branchCode}
                    error={formik.touched.branchCode ? formik.errors.branchCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledDropdown
                    id='metalType'
                    name='metalType'
                    label='Metal Type *:'
                    options={metalTypeOptions}
                    value={
                        metalTypeOptions.find(
                            (option) => option.value === formik.values.metalType
                        )?.label ?? ''
                    }
                    error={
                        formik.touched.metalType && formik.errors.metalType
                            ? formik.errors.metalType
                            : undefined
                    }
                    onSelectOption={(option) => {
                        formik.setFieldValue('metalType', option.value);

                        dispatch(combinedActions.goldSetInventoryFilterParams({
                            ...filters,
                            branchCode: formik.values.branchCode,
                            metalType: Number(option.value),
                        }));

                        dispatch(combinedActions.goldGetInventoryStockListingAttempt());
                    }}
                    // onBlur={formik.handleBlur}
                    optionSelected={!!formik.values.metalType}
                    noOptionsMessage='No metal type found'
                />

                <div />

                <LabelledDropdown
                    id='productName'
                    name='productName'
                    label='Product Name* :'
                    options={productOptions}
                    value={productName}
                    error={formik.touched.productName ? formik.errors.productName : undefined}
                    onChange={handleOnSearchProduct}
                    onSelectOption={handleOnSelectProduct}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No product found'
                    autoComplete='off'
                    optionSelected={!!productOptions.find((p) => p.label === productName)}
                />

                <div className={FormStyles['available-balance']}>
                    <Text label='Available Amount:' />
                    <Text
                        label={String(
                            selectedProduct?.physicalGoldWeight ||
                            selectedProduct?.physicalSilverWeight ||
                            selectedProduct?.paperGoldWeight ||
                            selectedProduct?.paperSilverWeight ||
                            '-'
                        )}
                        isBold
                    />
                </div>

                <LabelledInput
                    id='amount'
                    name='amount'
                    label='Amount* :'
                    value={formik.values.amount}
                    error={formik.touched.amount ? formik.errors.amount : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!selectedProduct || isPhysicalInventoryItem(selectedProduct)}
                />
            </div>

            {renderSerialNumberList()}

            <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                <div className={FormStyles['button-subcontainer']}>
                    <ActionButton
                        label='Create Stock Out'
                        type='submit'
                        isLoading={isLoading}
                        disabled={isLoading}
                    />
                </div>
            </div>

            <ConfirmStockoutTransactionsModal isOpen={isOpen} onClose={handleCloseModal} isLoading={isCheckTransactions} />

        </form>
    );
};

export default CreateStockOutForm;
